import React, { useEffect, useRef, useState } from 'react';
import { Container, Typography, CircularProgress, Box, Card, CardContent, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUserAccount, verifyKlarnaSession, superStatusToLocal, paymentFailureMessage } from './api';
import { db } from '../../FirebaseConfig';
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { stripePromise } from './stripeClient';



const API_BASE = process.env.REACT_APP_API_BASE || 'https://innovators.onrender.com';

const ExtravagantContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
}));

const ExtravagantCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
  overflow: 'hidden',
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(8px)',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(90deg, #ffaf7b, #d76d77)',
  padding: theme.spacing(3),
  textAlign: 'center',
  color: '#fff',
}));

const ConfirmationPage = () => {
  const stripe = useStripe();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [confirmationError, setConfirmationError] = useState('');
  const [transactionComplete, setTransactionComplete] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const ranRef = useRef(false);

  useEffect(() => {
    const run = async () => {
      if (ranRef.current) return;
      if (!stripe) return;
      ranRef.current = true;

      const storedData = sessionStorage.getItem('paymentUserData');
      if (!storedData) {
        setConfirmationError('User data not found. Please try again.');
        setLoading(false);
        return;
      }
      const userData = JSON.parse(storedData);
      setUserEmail(userData.email || '');

      const params = new URLSearchParams(window.location.search);
      const piClientSecret = params.get('payment_intent_client_secret');
      const piIdFromUrl = params.get('payment_intent');
      const redirectStatus = params.get('redirect_status');

      const superReturn = params.get('super');

      try {
        let status;
        let paymentOption;
        let amountMinor;
        let providerRef = null;
        let failureCode = '';

        if (superReturn) {
          // Super Payments hosted checkout. The ?super= value says which URL we
          // came back on, but the real outcome is read from the API — a customer
          // can land on successUrl without the money having moved.
          const txId = userData.superTransactionId;
          if (!txId) throw new Error('Missing Super transaction reference.');

          const res = await fetch(`${API_BASE}/super/payment/${encodeURIComponent(txId)}`);
          const tx = await res.json();
          if (!res.ok) throw new Error(tx?.error || 'Could not verify the Super payment.');

          status = superStatusToLocal(tx.transactionStatus);

          paymentOption = tx.fundingTypes?.includes('BUY_NOW_PAY_LATER') ? 'super-paylater' : 'super';
          amountMinor =
            typeof tx.transactionAmount === 'number'
              ? tx.transactionAmount
              : Math.round(Number(userData.firstPaymentAmount || 0) * 100);
          providerRef = tx.transactionId || txId;
          failureCode = tx.transactionStatus || '';
        } else {
          let paymentIntent = null;
          if (piClientSecret) {
            const res = await stripe.retrievePaymentIntent(piClientSecret);
            paymentIntent = res?.paymentIntent || null;
          } else if (piIdFromUrl) {
            if (redirectStatus !== 'succeeded') {
              throw new Error(`Payment ${redirectStatus || 'not successful'}`);
            }
          }

          if (!paymentIntent && redirectStatus !== 'succeeded') {
            throw new Error('Unable to verify payment status.');
          }

          status =
            paymentIntent?.status || (redirectStatus === 'succeeded' ? 'succeeded' : 'unknown');

          const methodType = (paymentIntent?.payment_method_types?.[0] || '').toLowerCase();
          paymentOption =
            methodType === 'afterpay_clearpay'
              ? 'clearpay'
              : methodType === 'klarna'
              ? 'klarna'
              : methodType || 'stripe';

          amountMinor =
            typeof paymentIntent?.amount === 'number'
              ? paymentIntent.amount
              : Math.round(Number(userData.firstPaymentAmount || 0) * 100);
          providerRef = paymentIntent?.id || piIdFromUrl || null;
          failureCode = paymentIntent?.last_payment_error?.code || redirectStatus || '';
        }

        if (userData.paymentDocId) {
          await updateDoc(doc(db, 'payments', userData.paymentDocId), {
            status,
            stripePaymentIntentId: providerRef,
            amountMinor,
            amountMajor: amountMinor / 100,
            method: paymentOption,
            updatedAt: serverTimestamp(),
            updatedAtISO: new Date().toISOString(),
          });
        }

        if (status === 'succeeded') {
          const accountResult = await createUserAccount(
            {
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              phone: userData.phone,
            },
            {
              amountPaid: amountMinor,
              productId: userData.productId || null,
              source: paymentOption,
              currency: "GBP",
              // Carried through the redirect in sessionStorage. Without these the
              // payments@ alert says no course and the affiliate never gets credited.
              courseKey: userData.course || '',
              referralCode: userData.referralCode || '',
              staff: userData.staff || '',
            }
          );

          await addDoc(collection(db, 'orders'), {
            name: `${userData.firstName ?? ''} ${userData.lastName ?? ''}`.trim(),
            email: userData.email,
            phone: userData.phone,
            staff: userData.staff || '',
            paymentPlan: userData.paymentPlan || 'part',
            paymentOption,
            amountPaid: amountMinor / 100,
            productName: userData.course || '',
            productId: userData.productId || null,
            stripePaymentIntentId: providerRef,
            status: accountResult?.success ? 'completed' : 'failed',
            failureReason: accountResult?.success ? '' : accountResult?.error || 'Account/order creation failed',
            createdAt: serverTimestamp(),
            createdAtISO: new Date().toISOString(),
          });

          if (accountResult?.success) {
            setTransactionComplete(true);
            sessionStorage.removeItem('paymentUserData');
            window.history.replaceState({}, document.title, window.location.pathname);
          } else {
            setConfirmationError(accountResult?.error || 'Failed to create account.');
          }
        } else if (status === 'processing') {
          setConfirmationError("Your payment is processing. You'll get access as soon as it completes.");
        } else if (
          status === 'requires_payment_method' ||
          status === 'canceled' ||
          redirectStatus === 'failed' ||
          redirectStatus === 'canceled'
        ) {
          setConfirmationError(paymentFailureMessage(paymentOption, failureCode));
        } else {
          setConfirmationError(`Payment status: ${status}`);
        }
      } catch (err) {
        setConfirmationError('Payment verification failed: ' + (err?.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [stripe]);

  const handleGoHome = () => {
    navigate(sessionStorage.getItem('payReturnTo') || '/');
  };

  return (
    <ExtravagantContainer maxWidth="full">
      <ExtravagantCard>
        <HeaderBox>
          <Typography variant="h3" component="h1">
            Payment Confirmation
          </Typography>
        </HeaderBox>
        <CardContent sx={{ p: 4 }}>
          {loading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : transactionComplete ? (
            <Box textAlign="center">
              <Typography variant="h5" gutterBottom>
                Your payment has been completed and your account has been created successfully!
              </Typography>
              <Typography variant="body2" mt={2}>
                A confirmation has been sent to <strong>{userEmail}</strong>
              </Typography>

              {/* NEW: Digital rights waiver notice — shown prominently on success */}
              <Box
                sx={{
                  mt: 3,
                  p: 2.5,
                  borderRadius: 2,
                  border: '2px solid #1565c0',
                  background: 'rgba(21, 101, 192, 0.07)',
                  textAlign: 'left',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 900,
                    color: '#1565c0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    mb: 1,
                  }}
                >
                  Important Notice Regarding Your Right to Cancel
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.8, color: '#1a1a1a' }}>
                  By purchasing this digital content, you requested immediate access and acknowledged that your right to
                  cancel under the{' '}
                  <strong>
                    Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013
                  </strong>{' '}
                  is lost once download or access begins.
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                onClick={handleGoHome}
                sx={{ mt: 3 }}
              >
                Go to Home
              </Button>
            </Box>
          ) : (
            <Box textAlign="center">
              <Typography variant="body1" color="error" align="center">
                {confirmationError}
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleGoHome}
                sx={{ mt: 2 }}
              >
                Return to Payment
              </Button>
            </Box>
          )}
        </CardContent>
      </ExtravagantCard>
    </ExtravagantContainer>
  );
};

const StripeWrapperConfirm = () => (
  <Elements stripe={stripePromise}>
    <ConfirmationPage />
  </Elements>
);

export default StripeWrapperConfirm;